import { styled, css } from "styled-components";
import { padding, rgba } from "polished";
import { checkboxEffect } from "../../animations/effects.es.js";
const paddingMap = {
  xsmall: 2,
  small: 4,
  medium: 8
};
const CheckboxWrapper = styled("label")(
  ({ theme }) => ({
    boxSizing: "border-box",
    color: theme.color.gray8,
    cursor: "pointer",
    display: "flex",
    fontFamily: theme.fontFamily.default,
    fontSize: theme.fontSize.default,
    lineHeight: theme.lineHeight.default,
    listStyle: "none",
    margin: 0,
    outline: 0,
    padding: 0,
    position: "relative",
    verticalAlign: "middle"
  }),
  ({ $disabled, theme }) => $disabled ? {
    color: theme.color.gray6,
    cursor: "default"
  } : void 0,
  ({ $noWrap }) => ({
    whiteSpace: $noWrap ? "nowrap" : void 0
  }),
  ({ $paddingX, $paddingY }) => padding(paddingMap[$paddingY], paddingMap[$paddingX])
);
const CheckboxDisplay = styled("span")(
  ({ theme }) => ({
    backgroundColor: "#ffffff",
    borderCollapse: "separate",
    borderColor: theme.controlBorderColor,
    borderRadius: 2,
    borderStyle: "solid",
    borderWidth: 1,
    boxSizing: "border-box",
    flex: "0 0 auto",
    height: 16,
    margin: "2px 0",
    position: "relative",
    transition: "all 0.3s",
    width: 16,
    "&::before": {
      borderColor: "#ffffff",
      borderStyle: "solid",
      borderWidth: "0 2px 2px 0",
      boxSizing: "border-box",
      content: JSON.stringify(""),
      display: "table",
      height: 16 / 14 * 8,
      left: "22%",
      opacity: 1,
      position: "absolute",
      top: "46%",
      width: 16 / 14 * 5
    }
  }),
  ({ $checked, $disabled, $disableHoverStyles, $indeterminate, theme }) => $checked && !$indeterminate ? {
    backgroundColor: theme.color.blue6,
    borderColor: "transparent",
    "&::before": {
      opacity: 1,
      transform: "rotate(45deg) scale(1) translate(-50%, -50%)",
      transition: "all .2s cubic-bezier(.12, .4, .29, 1.46) .1s"
    },
    "&:focus-within": {
      boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.25)}`
    },
    [`${CheckboxWrapper}:hover &&`]: {
      backgroundColor: !$disabled && !$disableHoverStyles ? theme.color.blue8 : void 0
    },
    [`${CheckboxWrapper}:active &&`]: {
      backgroundColor: !$disabled ? theme.color.blue8 : void 0
    }
  } : {
    "&::before": {
      opacity: 0,
      transform: "rotate(45deg) scale(0) translate(-50%, -50%)",
      transition: "all .1s cubic-bezier(.71, -0.46, .88, .6),opacity .1s"
    },
    "&:focus-within": {
      boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.05)"
    },
    [`${CheckboxWrapper}:hover &&`]: {
      borderColor: !$disabled && !$disableHoverStyles ? theme.color.blue6 : void 0
    },
    [`${CheckboxWrapper}:active &&`]: {
      borderColor: !$disabled ? theme.color.blue8 : void 0
    }
  },
  ({ $disabled, $indeterminate, theme }) => $indeterminate ? {
    backgroundColor: "#ffffff",
    "&::before": {
      borderColor: theme.color.blue6,
      borderWidth: 4,
      boxSizing: "content-box",
      content: JSON.stringify(""),
      display: "block",
      height: 0,
      left: "50%",
      opacity: 1,
      position: "absolute",
      top: "50%",
      transform: "translate(-50%, -50%) scale(1)",
      width: 0
    },
    [`${CheckboxWrapper}:active &&`]: {
      "&::before": {
        borderColor: !$disabled ? theme.color.blue8 : void 0
      }
    }
  } : void 0,
  ({
    $checked,
    $disabled,
    $disableHoverStyles,
    $indeterminate,
    theme,
    $tone
  }) => $tone === "critical" && !$disabled ? {
    backgroundColor: $checked && !$indeterminate ? theme.color.red6 : void 0,
    borderColor: theme.color.red6,
    "&::before": {
      borderColor: $indeterminate ? theme.color.red6 : void 0
    },
    [`${CheckboxWrapper}:active &&::before`]: {
      borderColor: $indeterminate ? theme.color.red6 : void 0
    },
    "&:focus-within": {
      boxShadow: `0 0 0 2px ${rgba(theme.color.red6, 0.25)}`
    },
    ...!$disableHoverStyles ? {
      [`${CheckboxWrapper}:hover &&`]: {
        backgroundColor: $checked && !$indeterminate ? theme.color.red6 : void 0,
        borderColor: theme.color.red6
      }
    } : void 0
  } : void 0,
  ({ $disabled, theme }) => $disabled ? {
    backgroundColor: theme.color.gray3,
    borderColor: theme.controlBorderColor,
    "&::before": {
      borderColor: theme.color.gray6
    }
  } : void 0,
  css`
    &::after {
      animation: ${checkboxEffect} 0.36s ease-in-out;
      animation-fill-mode: forwards;
      border: ${({ theme, $tone }) => `1px solid ${$tone === "critical" ? theme.color.red6 : theme.color.blue6}`};
      border-radius: 2px;
      content: ${({ animate }) => animate ? '""' : void 0};
      height: 100%;
      left: -1px;
      position: absolute;
      top: -1px;
      width: 100%;
    }
  `
);
const CheckboxInput = styled("input").attrs({
  type: "checkbox"
})({
  boxSizing: "border-box",
  cursor: "inherit",
  left: -1,
  height: "calc(100% + 2px)",
  margin: 0,
  opacity: 0,
  position: "absolute",
  top: -1,
  width: "calc(100% + 2px)"
});
const CheckboxContent = styled("div")({
  flex: "1 1 0",
  overflowX: "hidden",
  paddingLeft: 8
});
const CheckboxTitle = styled("h5")(({ theme }) => ({
  color: theme.color.gray9,
  font: "inherit",
  fontWeight: theme.fontWeight.medium,
  margin: 0,
  overflowX: "hidden",
  textOverflow: "ellipsis"
}));
const CheckboxText = styled("div")(
  {
    margin: 0,
    overflowX: "hidden",
    textOverflow: "ellipsis"
  },
  ({ hasTitle, theme }) => hasTitle ? {
    color: theme.color.gray7
  } : void 0
);
export {
  CheckboxContent,
  CheckboxDisplay,
  CheckboxInput,
  CheckboxText,
  CheckboxTitle,
  CheckboxWrapper
};
